// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-COST-6 v2.4 — GitHub cost for the month: net billing by product/SKU (what Owl actually pays after the
// plan's included usage) and an ESTIMATED split of the Actions cost by workflow (run wall time as weight).

import Box from '@mui/material/Box'
import Notice from '../../../components/ui/Notice'
import { DataGrid, type DataGridColumn } from '../../../components/DataGrid'
import type { FinanceSummary } from '../../../api/finance'
import { money } from './FinanceTables'

type Detail = NonNullable<FinanceSummary['detail']>
type Line = Detail['github_lines'][number]
type Workflow = Detail['github_workflows'][number]

const lineCols: DataGridColumn<Line>[] = [
  { key: 'product', header: 'Product', value: l => l.product, render: l => <b>{l.product}</b> },
  { key: 'sku', header: 'SKU', value: l => l.sku ?? '', render: l => l.sku ?? '—' },
  { key: 'repo', header: 'Repository', value: l => l.repo ?? '', render: l => l.repo ?? '—' },
  { key: 'qty', header: 'Usage', align: 'right', value: l => l.quantity, render: l => l.quantity === null ? '—' : `${l.quantity.toLocaleString()} ${l.unit ?? ''}` },
  { key: 'net', header: 'Net cost', align: 'right', value: l => l.net_usd, render: l => <b>{money(l.net_usd)}</b> },
]

const wfCols: DataGridColumn<Workflow>[] = [
  { key: 'workflow', header: 'Workflow', minWidth: 220, value: w => w.workflow, render: w => <b>{w.workflow}</b> },
  { key: 'runs', header: 'Runs', align: 'right', value: w => w.runs },
  { key: 'minutes', header: 'Run time (min)', align: 'right', value: w => w.minutes, render: w => w.minutes.toLocaleString() },
  { key: 'est', header: 'Est. share of Actions cost', align: 'right', value: w => w.est_usd, render: w => money(w.est_usd) },
]

export default function GithubPanel({ detail }: { detail: Detail }) {
  if (!detail.github_available)
    return <Notice severity="info">GitHub billing isn’t connected yet, so GitHub shows “—” in expenses (not $0). Connect it under <b>Data sources</b>.</Notice>
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box>
        <Box sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Billing — {money(detail.expenses.github_usd)} net this month</Box>
        {detail.github_lines.length === 0
          ? <Box sx={{ fontSize: 12.5, color: 'text.secondary' }}>GitHub reported no usage for this month.</Box>
          : <DataGrid rows={detail.github_lines} columns={lineCols} storageKey="finance-v2-github-lines"
              getRowKey={l => `${l.product}:${l.sku}:${l.repo}`} dense />}
      </Box>
      <Box>
        <Box sx={{ fontWeight: 700, fontSize: 13 }}>Actions cost by workflow — estimate</Box>
        <Box sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
          GitHub does not bill per workflow. This splits the month’s Actions net cost by each workflow’s run time.
        </Box>
        {detail.github_workflows.length === 0
          ? <Box sx={{ fontSize: 12.5, color: 'text.secondary' }}>No workflow runs recorded for this month.</Box>
          : <DataGrid rows={detail.github_workflows} columns={wfCols} storageKey="finance-v2-github-workflows" getRowKey={w => w.workflow} dense />}
      </Box>
    </Box>
  )
}
